import type {
  NrtTestResult,
  NrtTestResultItem,
  StartTestMappingsRequest,
  TestMappingsStatusResponse
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { buildInitialNrtTestResult } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  NRT_LAMBDA_BASE_PATH,
  readErrorMessage
} from "./ddc-pages-nrt-database-filter-tool-fetch";
import nrtTestSampleResponse from "./testresult.json";

// A real MAPPING_TEST response, checked in verbatim. Now a TEST FIXTURE ONLY —
// nothing at runtime reads it since the mock was replaced by the live run.
export const nrtTestSampleItems =
  nrtTestSampleResponse.data as NrtTestResultItem[];

export const POLL_INTERVAL_MS = 2000;

// 120s downstream timeout + slack. Attempts, not wall-clock, so fake timers stay deterministic.
export const MAX_POLL_ATTEMPTS = 75;

const TIMEOUT_MESSAGE =
  "The test run did not finish in time. It may still be processing — try again.";

const nowTimestamp = (): string => String(Math.floor(Date.now() / 1000));

const errorResult = (message: string, executionId?: string): NrtTestResult => ({
  status: "ERROR",
  timestamp: nowTimestamp(),
  message,
  items: [],
  ...(executionId ? { executionId } : {})
});

// Resolves early on abort, and detaches its own abort listener on a normal
// timeout too — otherwise a long poll loop leaks one listener per interval.
const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

const startRun = async (
  request: StartTestMappingsRequest,
  signal?: AbortSignal
): Promise<{ executionId: string } | { message: string }> => {
  const response = await fetch(`${NRT_LAMBDA_BASE_PATH}/testMappings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal
  });

  if (!response.ok) {
    return {
      message: await readErrorMessage(response, "Test could not start")
    };
  }

  const body = (await response.json()) as { executionId?: unknown };
  if (typeof body.executionId !== "string" || body.executionId === "") {
    return { message: "The test run could not be started." };
  }
  return { executionId: body.executionId };
};

const pollOnce = async (
  executionId: string,
  signal?: AbortSignal
): Promise<TestMappingsStatusResponse | { message: string } | null> => {
  try {
    const response = await fetch(
      `${NRT_LAMBDA_BASE_PATH}/testMappingsStatus?executionId=${encodeURIComponent(executionId)}`,
      { signal }
    );
    if (response.ok) {
      return (await response.json()) as TestMappingsStatusResponse;
    }
    // 5xx is presumed transient, so keep polling; 4xx never resolves on retry.
    if (response.status < 500) {
      return {
        message: await readErrorMessage(
          response,
          "Could not read the test result"
        )
      };
    }
    return null;
  } catch {
    // A rejected fetch (network blip, DNS hiccup) is transient exactly like
    // a 5xx status — consume this attempt instead of aborting the run.
    return null;
  }
};

/**
 * Starts a real test run and polls until it reaches a terminal state.
 *
 * NEVER REJECTS — every failure becomes `{ status: "ERROR", message }`, because
 * RecordModal and TestResultPanel depend on that and their specs assert it.
 *
 * An aborted run (the record was closed mid-flight) resolves to the IDLE
 * result so nothing from it is ever displayed.
 */
export const runNrtRecordTestCheck = async (
  { accountId, providerId }: StartTestMappingsRequest,
  options?: { signal?: AbortSignal }
): Promise<NrtTestResult> => {
  const signal = options?.signal;

  try {
    const started = await startRun({ accountId, providerId }, signal);
    if (signal?.aborted) return buildInitialNrtTestResult();
    if ("message" in started) return errorResult(started.message);

    const { executionId } = started;

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      // Sequential by nature: each poll depends on the previous not being terminal.
      // eslint-disable-next-line no-await-in-loop
      await wait(POLL_INTERVAL_MS, signal);
      if (signal?.aborted) return buildInitialNrtTestResult();

      // eslint-disable-next-line no-await-in-loop
      const status = await pollOnce(executionId, signal);
      if (signal?.aborted) return buildInitialNrtTestResult();

      // A transient (5xx) poll failure just consumes this attempt, not the whole run.
      if (!status) continue;

      // Checked via `status`, not `message` — TestMappingsStatusResponse has its own.
      if (!("status" in status)) {
        return errorResult(status.message, executionId);
      }

      if (status.status === "COMPLETE") {
        return {
          status: "COMPLETE",
          timestamp: nowTimestamp(),
          message: status.message,
          items: status.items,
          executionId,
          ...(status.hasNoActiveFilters ? { hasNoActiveFilters: true } : {})
        };
      }

      if (status.status === "ERROR") {
        return errorResult(
          status.message ?? "The test run failed.",
          executionId
        );
      }
    }

    // No server-side expiry besides the 7-day S3 lifecycle — this ceiling is the only guard.
    return errorResult(TIMEOUT_MESSAGE, executionId);
  } catch (error) {
    if (signal?.aborted) return buildInitialNrtTestResult();
    const message = error instanceof Error ? error.message : "unknown error";
    return errorResult(
      `Test could not complete due to a system error: ${message}`
    );
  }
};
